import { forwardRef, useEffect, useRef, useImperativeHandle } from 'react';

import { postGetVoice, postGetNpcPrologueVoice } from '../../../api';
import { useAppSelector } from '../../../state';
import { AudioStatus, IGetVoiceReq } from '../../../types';

export interface CustomAudioPayload {
  mid: string;
  npcId?: number;
  url?: string;
  status?: AudioStatus;
  fromWorkspace?: boolean;
  isOepn?: boolean;
  customMid?: string;
}

interface ICustomAudioProps {
  onAudioPlayEnd: (mid: string) => void;
  onAudioPause: (mid: string) => void;
  onAudioPlaying: (mid: string) => void;
  onAudioWaiting: (mid: string) => void;
  onAudioError: (mid: string, msg?: string) => void;
}

export const CustomAudio = forwardRef((props: ICustomAudioProps, ref) => {
  const { npcId: _npcId, conversationNpcId } = useAppSelector((state) => state.edit);
  const npcId = conversationNpcId || _npcId;

  const audioRef = useRef<
    HTMLAudioElement & {
      mid: string;
      npcId: number;
    }
  >(null);

  const getVoice = async (payload: CustomAudioPayload) => {
    try {
      let voiceUri = '';

      if (payload.isOepn) {
        const { prologue_voice_url } = await postGetNpcPrologueVoice({
          npc_id: npcId,
          from_workspace: payload.fromWorkspace,
        } as IGetVoiceReq);
        if (!prologue_voice_url) {
          props.onAudioError(payload.mid);
          return;
        } else {
          voiceUri = prologue_voice_url;
        }
      } else {
        const { voice_uri } = await postGetVoice({
          npc_id: npcId,
          im_mid: payload.mid,
          custom_mid: payload.customMid,
          from_workspace: payload.fromWorkspace,
        } as IGetVoiceReq);
        if (!voice_uri) {
          props.onAudioError(payload.mid);
          return;
        } else {
          voiceUri = voice_uri;
        }
      }

      play({
        url: voiceUri,
        mid: payload.mid,
      });
    } catch (err: any) {
      props.onAudioError(payload.mid, err?.status_msg || err?.message);
    }
  };

  const onAudioPlayEnd = () => {
    if (audioRef.current) {
      console.log('audio play end', audioRef.current.mid);
      props.onAudioPlayEnd(audioRef.current.mid);
    }
  };

  const onAudioPause = () => {
    if (audioRef.current) {
      console.log('audio play pause', audioRef.current.mid);
      props.onAudioPause(audioRef.current.mid);
    }
  };

  const onAudioPlaying = () => {
    if (audioRef.current) {
      props.onAudioPlaying(audioRef.current.mid);
    }
  };

  const onAudioWaiting = () => {
    if (audioRef.current) {
      props.onAudioWaiting(audioRef.current.mid);
    }
  };

  const play = (payload: CustomAudioPayload) => {
    const { mid, url } = payload;

    if (audioRef.current) {
      audioRef.current.mid = mid;

      if (!url || payload.fromWorkspace) {
        getVoice(payload);
        props.onAudioWaiting(audioRef.current.mid);
      } else {
        audioRef.current.src = url;
        audioRef.current.play();
      }
    }
  };

  const pause = (payload: CustomAudioPayload) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  useImperativeHandle(ref, () => ({
    play,
    pause,
  }));

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current?.addEventListener('playing', onAudioPlaying);
      audioRef.current?.addEventListener('ended', onAudioPlayEnd);
      audioRef.current?.addEventListener('pause', onAudioPause);
      audioRef.current?.removeEventListener('waiting', onAudioWaiting);
    }

    return () => {
      audioRef.current?.removeEventListener('playing', onAudioPlaying);
      audioRef.current?.removeEventListener('ended', onAudioPlayEnd);
      audioRef.current?.removeEventListener('pause', onAudioPause);
      audioRef.current?.removeEventListener('waiting', onAudioWaiting);
    };
  }, [onAudioPlayEnd, onAudioPlaying, onAudioWaiting, onAudioPause]);

  return (
    <audio
      controls
      ref={audioRef}
      style={{ position: 'absolute', top: -1000, left: -1000 }}
      preload="auto"
    />
  );
});
